import { useEffect, useState } from "react";
import { AlertTriangle, ArrowRight, Zap, Shield } from "lucide-react";
import { api } from "@/lib/api";

/**
 * MostCriticalDependencyBanner — Doctrine 3Q sur la cartographie.
 *
 * V23.15.32 — Répond aux 3 questions dans un encart HAUT-DE-VUE :
 *  - Et alors ?        → "Impact estimé X CHF si l'acteur défaille"
 *  - Pourquoi ?        → substituabilité + criticité, fraîcheur
 *  - Que fais-je ?     → CTA ouvre Annuaire/Gouvernance/Graphe
 *
 * S'affiche en tête des vues Cartographie, Ontologie, Graphe multi-niveaux.
 * Silencieux si aucune dépendance dans le workspace (mode additif strict).
 */
export default function MostCriticalDependencyBanner({ className = "" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/workspace/dependencies/most-critical-now");
        if (!cancelled) setData(r.data);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading || !data || !data.node) return null;

  const n = data.node;
  const nType = (n.type || "").toLowerCase();
  const isRegulateur = nType === "regulateur";
  const color = isRegulateur ? "#E24B4A" : "#EF9F27";
  const Icon = isRegulateur ? Shield : AlertTriangle;

  return (
    <div
      className={`rounded-xl border p-4 ${className}`}
      style={{
        background: `linear-gradient(135deg, ${color}12 0%, transparent 70%)`,
        borderColor: `${color}44`,
      }}
      data-testid="most-critical-dependency-banner"
    >
      <div className="flex items-start gap-3">
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 relative"
          style={{ background: `${color}22`, border: `1px solid ${color}66` }}
        >
          <Icon className="w-5 h-5" style={{ color }} />
          <div
            className="absolute inset-0 rounded-lg animate-ping"
            style={{ border: `1px solid ${color}`, opacity: 0.3 }}
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-widest font-semibold" style={{ color }}>
              Dépendance la plus critique en ce moment
            </span>
            <span className="text-[10px] text-zinc-500 tabular-nums">
              (score composite {data.score_composite})
            </span>
          </div>
          <div className="text-lg font-bold text-white mt-1" data-testid="critical-dep-name">
            {n.name}
            {n.type && (
              <span className="text-xs text-zinc-500 font-normal ml-2">· {n.type}</span>
            )}
          </div>
          <div className="text-sm text-zinc-200 mt-1.5 leading-relaxed" data-testid="critical-dep-narrative">
            {data.narrative}
          </div>
          <div className="text-xs text-zinc-400 mt-1 italic" data-testid="critical-dep-why">
            <Zap className="w-3 h-3 inline mr-1 text-[#FFB627]" />
            {data.why_now}
          </div>
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <a
              href={data.cta.href}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md font-medium transition-colors"
              style={{
                background: `${color}22`,
                border: `1px solid ${color}66`,
                color,
              }}
              data-testid="critical-dep-cta"
            >
              {data.cta.label}
              <ArrowRight className="w-3 h-3" />
            </a>
            {data.impact_estime_meur > 0 && (
              <span className="text-[11px] text-zinc-500">
                <span className="font-semibold text-zinc-300">{data.impact_display}</span> d&apos;exposition estimée
              </span>
            )}
            {data.total_dependencies > 1 && (
              <span className="text-[10px] text-zinc-600">
                · sur {data.total_dependencies} dépendances cartographiées
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
